// Conversion between the payment's own currency and EUR, for the amount of an
// envelope tag: the operator types the payment's currency, the wire carries
// EUR (amount_eur_minor).
//
// The rate is the pair the envelope hint already returns for the payment's
// current input: the header in the payment's currency and that header's EUR
// (header_eur_minor). That is the rate the server converts the payment with,
// so the whole payment maps to exactly its EUR, to the minor unit, and a part
// maps in proportion: round(x · headerEURMinor / headerMinor).
//
// `currency` travels with the pair so a caller holding the pair of one
// currency can refuse it for another — the form changes currency before the
// hint for the new one arrives.
//
// Integers in minor units throughout. A product of two amounts is computed in
// BigInt and divided once, rounding half up on the magnitude with the sign
// restored: floor(|r| + 1/2), as internal/money's roundRat does, so a half
// falls the same way on both sides. A double cannot be trusted with that: once
// the product passes 2^53 it cannot tell an exact half from a hair under it.
//
// An amount must be an exact integer; BigInt throws on anything else, and
// that is kept — a caller's bug is not turned into a different amount (the
// policy of money.ts).
//
// Results come back through Number(...). That is exact for every amount up to
// the whole payment (toEUR of an amount within the header stays within the
// header's EUR, fromEUR likewise); an amount far over the payment can convert
// past 2^53 − 1, where the result is only the nearest double — compare an
// amount with the header, both exact, before relying on its conversion.

/**
 * The conversion pair: `headerMinor` minor units of `currency` are worth
 * `headerEURMinor` minor units of EUR.
 */
export interface FxBasis {
  currency: string;
  headerMinor: number;
  headerEURMinor: number;
}

const isPositiveSafeInteger = (n: number | null): n is number =>
  n !== null && Number.isSafeInteger(n) && n > 0;

/**
 * The pair, or null unless both sides are positive safe integers: a side not
 * known yet, a header of 0, and a header whose EUR rounds to 0 (a few dinars)
 * are all «no rate» — never a division by zero further on.
 */
export function fxBasis(
  currency: string,
  headerMinor: number | null,
  headerEURMinor: number | null,
): FxBasis | null {
  if (!isPositiveSafeInteger(headerMinor) || !isPositiveSafeInteger(headerEURMinor)) return null;
  return { currency, headerMinor, headerEURMinor };
}

/**
 * round(num / den) for den > 0: half up on the magnitude, the sign restored
 * on the BigInt (a BigInt has no -0; a double would). BigInt division
 * truncates and the magnitude is not negative, so (2m + den) / (2·den) is
 * floor(m / den + 1/2).
 */
function roundDiv(num: bigint, den: bigint): bigint {
  const negative = num < 0n;
  const m = negative ? -num : num;
  const q = (2n * m + den) / (2n * den);
  return negative ? -q : q;
}

/**
 * `x` minor units of the payment's currency as EUR minor units:
 * round(x · headerEURMinor / headerMinor).
 */
export function toEUR(x: number, b: FxBasis): number {
  return Number(roundDiv(BigInt(x) * BigInt(b.headerEURMinor), BigInt(b.headerMinor)));
}

/**
 * `eur` EUR minor units as minor units of the payment's currency:
 * round(eur · headerMinor / headerEURMinor).
 */
export function fromEUR(eur: number, b: FxBasis): number {
  return Number(roundDiv(BigInt(eur) * BigInt(b.headerMinor), BigInt(b.headerEURMinor)));
}

/**
 * `eurCap` EUR as an amount in the payment's currency that does not convert
 * back to more than `eurCap`: fromEUR(eurCap), one unit less when rounding
 * carried it past the cap. That can only happen for a currency worth more
 * than EUR per minor unit: with 85 units standing for 100 of EUR, a cap of 3
 * EUR rounds to 3 units, which convert to 4 EUR — so the answer is 2. It is the
 * payment-currency figure for a EUR limit: shown as «at most», or used to cap
 * a default. A cap of nothing or less is 0.
 */
export function paymentWithin(eurCap: number, b: FxBasis): number {
  if (eurCap <= 0) return 0;
  const x = fromEUR(eurCap, b);
  return toEUR(x, b) > eurCap ? x - 1 : x;
}
